import { ColDef, ICellRendererParams } from "ag-grid-community";
import _ from "lodash";

import { ProposalColumnFnParams } from "lib/frontend/cm/utils/types";
import { getCurrencyField } from "lib/shared/currency/getCurrencyField";
import { getCustomFieldSuggestion, getCustomFieldValue } from "lib/shared/custom-fields";
import { CustomField, FieldType, Proposal, User } from "prisma/cm/client";

type ColDefWithType = ColDef<Proposal | User | Range>;

function getBaseCurrency(
  data: User | (Proposal & { owner: User }),
  modelContext: "User" | "Proposal" | "Range",
) {
  const currencyField = getCurrencyField(modelContext);
  return _.get(data, currencyField);
}

export function determineCellRendererAndParams(
  customField: CustomField,
  modelContext: "User" | "Proposal" | "Range",
  ops: {
    readonly?: boolean;
  } = {},
): {
  cellRenderer: ColDefWithType["cellRenderer"];
  cellRendererParams: ColDefWithType["cellRendererParams"];
} {
  const { isEditable: rawIsEditable } = customField;
  const isEditable = rawIsEditable && !ops.readonly;
  if (isEditable) {
    const wrongModelContext = customField.model !== modelContext;

    if (customField.valueType === FieldType.DOLLAR) {
      return {
        cellRenderer: "nominalInput",
        cellRendererParams: (params: ICellRendererParams) => {
          let suggestedValueProp = undefined;

          if (customField.enableSuggestions) {
            suggestedValueProp = getCustomFieldSuggestion(params.data, customField.id);
          }

          return {
            columnId: customField.id,
            isCustomColumnInput: !!customField.isEditable,
            baseCurrency: getBaseCurrency(params.data, modelContext),
            suggested: suggestedValueProp,
            disabled: wrongModelContext,
          };
        },
      };
    } else if (customField.valueType === FieldType.PERCENT) {
      return {
        cellRenderer: "numberInput",
        cellRendererParams: (params: ProposalColumnFnParams) => {
          const originalValue = getCustomFieldValue(params.data, customField.id);
          const suggestedValue = getCustomFieldSuggestion(params.data, customField.id);

          let suggestedValueProp = undefined;

          if (customField.enableSuggestions) {
            suggestedValueProp = {
              raw: suggestedValue,
              formatted:
                typeof suggestedValue === "number"
                  ? suggestedValue.toLocaleString()
                  : suggestedValue,
            };
          }

          return {
            overrideValue: originalValue ? Number(originalValue) * 100 : null,
            columnId: customField.id,
            isCustomColumnInput: !!customField.isEditable,
            valueType: customField.valueType,
            allowDecimal: true,
            suggestedValue: suggestedValueProp,
            disabled: wrongModelContext,
            hideControls: true,
            decimalScale: 2,
            fixedDecimalScale: false,
            allowDecimals: true,
            suffix: "%",
          };
        },
      };
    } else {
      return {
        cellRenderer: "input",
        cellRendererParams: (params: ProposalColumnFnParams) => {
          const suggestedValue = getCustomFieldSuggestion(params.data, customField.id);

          let suggestedValueProp = undefined;

          if (customField.enableSuggestions) {
            suggestedValueProp = {
              raw: suggestedValue,
              formatted:
                typeof suggestedValue === "number"
                  ? suggestedValue.toLocaleString()
                  : suggestedValue,
            };
          }

          return {
            columnId: customField.id,
            isCustomColumnInput: !!customField.isEditable,
            clearable: !_.isNil(customField.options),
            options: customField.options,
            valueType: customField.valueType,
            hideControls: customField.valueType !== FieldType.NUMBER,
            suggestedValue: suggestedValueProp,
            disabled: wrongModelContext,
          };
        },
      };
    }
  } else {
    return {
      cellRenderer: undefined,
      cellRendererParams: undefined,
    };
  }
}
